/**
 * Item page → Assessment → an opened value → "What lies behind this value"
 * (admins). The value's AI setting (and, for "provides", the standing rule and
 * whose name it accepts in), how the current value got there, and the agent's
 * own record of its latest reasoning: value, confidence, rationale, evidence.
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { LABEL_MONO } from '@/components/mono/primitives';
import { fetchValueDecisionRecords, type ValueDecisionRecord } from '@/lib/valueAiService';
import { VALUE_AI_MODE_LABEL, type ValueAiSetting, type ValueStatusResult } from '@/lib/valueStatus';

const OUTCOME: Record<string, string> = {
  accepted_as_proposed: 'accepted as proposed',
  accepted_with_change: 'accepted with a change',
  accepted_by_rule: 'accepted by the standing rule',
  rejected: 'rejected',
  reversed: 'later changed by a person',
  expired: 'expired',
  dismissed: 'dismissed',
};

export function ValueAiBehind({
  workspaceId,
  itemId,
  attributeKey,
  setting,
  agentName,
  nameOf,
  result,
}: {
  workspaceId: string;
  itemId: string;
  attributeKey: string;
  setting: ValueAiSetting | null;
  agentName: (agentType: string | null | undefined) => string;
  nameOf: (id: string | null | undefined) => string | null;
  result: ValueStatusResult;
}) {
  const [records, setRecords] = useState<ValueDecisionRecord[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    fetchValueDecisionRecords(workspaceId, itemId, attributeKey)
      .then((r) => live && setRecords(r))
      .catch((e) => live && setError(e instanceof Error ? e.message : String(e)));
    return () => {
      live = false;
    };
  }, [workspaceId, itemId, attributeKey]);

  const mode = setting?.mode ?? 'off';
  const ruleOwner = nameOf(setting?.setBy) ?? 'an admin';
  const latest = records?.[0];

  return (
    <div className="border border-[var(--rule)] bg-[var(--surface)] px-4 py-3" data-testid="value-ai-behind">
      <div className={`${LABEL_MONO} mb-2`}>What lies behind this value · admins</div>
      <div className="space-y-1.5 text-sm text-[var(--text-sec)]">
        <p>
          <strong className="text-[var(--text-pri)]">{VALUE_AI_MODE_LABEL[mode]}</strong>
          {setting?.agentType ? ` — ${agentName(setting.agentType)}` : ''}
          {mode === 'provide' && setting
            ? `. Standing rule set by ${ruleOwner} on ${new Date(setting.setAt).toLocaleDateString()}: its value is accepted in ${ruleOwner}'s name when it has been right at least ${Math.round(setting.acceptFloor * 100)}% of the time on this value, judged over at least 30 reviews by people.`
            : '.'}
          {setting?.reason ? ` “${setting.reason}”` : ''}{' '}
          <Link to={`/prioritization/settings/attributes/${encodeURIComponent(attributeKey)}`} className="underline">Change</Link>
        </p>
        {result.accepted && (
          <p>
            Current value set {new Date(result.accepted.acceptedAt).toLocaleDateString()} by{' '}
            {nameOf(result.accepted.acceptedBy) ?? 'someone'}
            {result.accepted.acceptanceMethod === 'standing_rule'
              ? ' through the standing rule'
              : result.fromProposal?.proposedBy?.startsWith('agent:')
                ? `, accepting ${result.fromProposal.proposedByName ?? 'an agent'}'s recommendation`
                : ''}
            .{result.accepted.acceptanceNote ? ` ${result.accepted.acceptanceNote}` : ''}
          </p>
        )}
        {error && <p className="text-[var(--coral)]">Could not read the agent's records: {error}</p>}
        {records && records.length === 0 && <p className="text-[var(--text-ter)]">No agent has worked on this value for this item yet.</p>}
        {latest && (
          <div className="mt-2 border-t border-[var(--rule)] pt-2">
            <p>
              Latest from {agentName(latest.agentType)} · {new Date(latest.createdAt).toLocaleDateString()}
              {latest.outputKind === 'proposal:value'
                ? ` · proposed ${JSON.stringify(latest.proposedValue)}`
                : ` · ${latest.outputKind.replace(/[:_]/g, ' ')}`}
              {latest.confidence != null ? ` · ${Math.round(latest.confidence * 100)}% sure` : ''}
              {latest.reviewOutcome ? ` · ${OUTCOME[latest.reviewOutcome] ?? latest.reviewOutcome}` : ' · not reviewed yet'}
            </p>
            {latest.rationale && <p className="mt-1 text-[var(--text-ter)]">{latest.rationale}</p>}
            {latest.evidence.length > 0 && (
              <ul className="mt-1 list-disc pl-5 text-xs text-[var(--text-ter)]">
                {latest.evidence.slice(0, 6).map((e, i) => (
                  <li key={e.id ?? i}>
                    {e.label ?? e.kind ?? 'evidence'}
                    {e.used ? ' — leaned on' : ''}
                  </li>
                ))}
              </ul>
            )}
            {latest.model && <p className="mt-1 text-xs text-[var(--text-dim)]">{latest.model}{latest.stackVersion ? ` · ${latest.stackVersion}` : ''}</p>}
          </div>
        )}
      </div>
    </div>
  );
}
